/**
 * Canary Status Panel
 * Displays canary deployment status and routing information
 */

import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Loading, ErrorMessage } from '@/components/common';
import { monitoringApi } from '@/lib/api/monitoring';
import type { DeploymentStatus } from '@/types/monitoring';
import { GitBranch, Server, ExternalLink, Copy, Check, Clock, Zap } from 'lucide-react';
import { useState } from 'react';

function formatUptime(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function StatusBadge({ status }: { status: DeploymentStatus['status'] }) {
  const colors = {
    healthy: 'bg-green-100 text-green-800',
    unhealthy: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
}

function DeploymentCard({ deployment, label }: { deployment: DeploymentStatus; label: string }) {
  const isCanary = deployment.track === 'canary';

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Server className="w-4 h-4" />
          {label}
        </h4>
        <StatusBadge status={deployment.status} />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Version</span>
          <span className="text-sm font-mono text-gray-900">{deployment.version}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Pod</span>
          <span className="text-sm font-mono text-gray-600 truncate max-w-[150px]" title={deployment.pod_name}>
            {deployment.pod_name}
          </span>
        </div>
        {deployment.uptime_seconds !== null && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Uptime
            </span>
            <span className="text-sm text-gray-900">{formatUptime(deployment.uptime_seconds)}</span>
          </div>
        )}
        {deployment.latency_ms !== null && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Zap className="w-3 h-3" /> Latency
            </span>
            <span className="text-sm text-gray-900">{deployment.latency_ms}ms</span>
          </div>
        )}
        {deployment.error && (
          <div className="text-xs text-red-600 bg-red-50 rounded p-2 mt-2">
            {deployment.error}
          </div>
        )}
      </div>

      <a
        href={deployment.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-1 text-xs ${
          isCanary ? 'text-purple-600 hover:text-purple-800' : 'text-blue-600 hover:text-blue-800'
        }`}
      >
        <ExternalLink className="w-3 h-3" />
        {deployment.external_url}
      </a>
    </div>
  );
}

export function CanaryStatusPanel() {
  const [copied, setCopied] = useState<string | null>(null);

  const { data: canaryInfo, isLoading, isError, error } = useQuery({
    queryKey: ['monitoring', 'canary'],
    queryFn: monitoringApi.getCanaryStatus,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (isLoading) {
    return (
      <Card className="bg-white">
        <Loading message="Loading deployment status..." />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="bg-white">
        <ErrorMessage error={error instanceof Error ? error.message : 'Failed to load deployment status'} />
      </Card>
    );
  }

  if (!canaryInfo) {
    return null;
  }

  const { stable, canary, routing } = canaryInfo;

  return (
    <Card className="bg-white">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Deployment Status</h3>
        </div>
        <div className="flex gap-2">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            Stable
          </span>
          {canary && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
              Canary
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Stable Deployment */}
        <DeploymentCard deployment={stable} label="Stable" />

        {/* Canary Deployment */}
        {canary ? (
          <DeploymentCard deployment={canary} label="Canary" />
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
            <p className="text-sm text-gray-400">Canary not deployed</p>
          </div>
        )}

        {/* Routing Info */}
        <div className="bg-blue-50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-blue-800">Canary Routing</h4>
          <div className="text-sm text-blue-700">
            <p className="mb-2">Add header to route to canary:</p>
            <div className="bg-white rounded border border-blue-200 p-2 font-mono text-xs flex items-center justify-between">
              <span>{routing.header_name}: {routing.header_value}</span>
              <button
                onClick={() => copyToClipboard(`${routing.header_name}: ${routing.header_value}`, 'header')}
                className="ml-2 p-1 hover:bg-blue-100 rounded"
              >
                {copied === 'header' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-blue-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Last checked: {new Date(stable.timestamp).toLocaleString()}
        </p>
      </div>
    </Card>
  );
}
